import { useCallback, useEffect, useMemo, useState } from 'react'
import { Pause, Play, Waves } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { fetchEventStream, DT_STR as S, type EventRow } from './devtoolsApi'

/**
 * A27.05 — Поток событий (live). FUNCTIONAL: a 5s polling table (with a pause
 * toggle) over the real `/admin/devtools/events`, which merges the newest
 * `updates` + `admin_audit` rows into one activity feed. Placeholder permission
 * USERS_VIEW; the endpoint enforces access.
 */
export function EventStreamScreen() {
  const [reloadKey, setReloadKey] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (paused) return
    const t = setInterval(() => setReloadKey((k) => k + 1), 5000)
    return () => clearInterval(t)
  }, [paused])

  const columns = useMemo<AdminColumn<EventRow>[]>(
    () => [
      { key: 'at', header: S.at, type: 'text' },
      { key: 'type', header: S.type, type: 'text' },
      { key: 'source', header: S.source, type: 'text' },
      { key: 'payload', header: S.payload, type: 'json' },
    ],
    [],
  )

  const fetcher = useCallback(fetchEventStream, [])
  return (
    <>
      <PageHeader
        title={S.eventStream}
        screenId="A27.05"
        actions={
          <div className="flex items-center gap-2">
            <span className="hidden text-xs text-secondary sm:inline">{S.livePolling}</span>
            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              {paused ? <Play size={13} /> : <Pause size={13} />}
              {paused ? S.resume : S.pause}
            </button>
          </div>
        }
      />
      <DataTable<EventRow>
        key={reloadKey}
        screenId="A27.05"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        emptyState={{ icon: <Waves size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
